import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { renderToString } from 'react-dom/server';
import { IconAsChunk } from '@boschebike/ebds-react-icons';
import Accordion from './Accordion.js';
import { handlePageScroll, handleTocScroll, appendRelativeToc, checkAutoExpand, getParsedToc, scrollToHash } from '../helpers/tocHelpers.js';

/**
 * Checks whether a heading has any children or not
 *
 * @param h the heading
 */
const hasChildren = h => h.children.length > 0;

/**
 * Checks if any child (at any level) of the current item is active or not.
 *
 * @param item the current heading
 * @param activeId the id of the active heading
 */
const hasActiveChild = (item, activeId) => item.children.some(c => c.id === activeId || hasActiveChild(c, activeId));

/**
 * Maps all ToC headings and their children into list items
 *
 * @param headings an array of headings at a given level
 */
const getHeadingList = headings => headings.map(c => /*#__PURE__*/React.createElement("li", {
  key: c.id
}, /*#__PURE__*/React.createElement("a", {
  href: `#${c.id}`
}, c.text), hasChildren(c) > 0 && /*#__PURE__*/React.createElement("ul", null, getHeadingList(c.children))));

/**
 * Maps ToC headings and their children into list items based on certain criterias
 *
 * @param headings an array of headings at a given level
 * @param activeId the id of the active heading
 * @param tocExpanded boolean stating if the ToC is fully expanded or not
 */
const getHeadingListConditionally = (headings, activeId, tocExpanded) => headings.map(c => {
  const isActiveHeading = c.id === activeId || hasActiveChild(c, activeId);
  const shouldExpand = hasChildren(c) && (tocExpanded || isActiveHeading);
  return /*#__PURE__*/React.createElement("li", {
    key: c.id,
    className: activeId === c.id ? 'active' : ''
  }, /*#__PURE__*/React.createElement("a", {
    href: `#${c.id}`
  }, /*#__PURE__*/React.createElement("span", null, c.text)), shouldExpand && /*#__PURE__*/React.createElement("ul", {
    "data-level": c.level + 1
  }, getHeadingListConditionally(c.children, activeId, tocExpanded)));
});

/**
 * Renders the relative ToC into and HTML string
 *
 * @param toc the toc list
 */
const getRelativeToc = (toc, tocHasContent) => renderToString(tocHasContent && /*#__PURE__*/React.createElement("div", {
  className: "ebds-toc-relative-container"
}, /*#__PURE__*/React.createElement(Accordion, null, /*#__PURE__*/React.createElement(Accordion.Item, {
  summary: "Table Of Contents",
  key: "toc"
}, /*#__PURE__*/React.createElement("ul", null, getHeadingList(toc[0].children))))));
const overFlownInitialState = {
  top: false,
  bottom: false,
  tested: false
};
const autoExpandInitialState = {
  active: true,
  tested: false
};
const TableOfContentsMain = /*#__PURE__*/forwardRef(({
  toc,
  tocHasContent,
  hash
}, ref) => {
  const [active, setActive] = useState(toc[0]?.id);
  const [overflown, setOverflown] = useState(overFlownInitialState);
  const [autoExpand, setAutoExpand] = useState(autoExpandInitialState);
  useEffect(() => {
    // Reset/update states at toc change
    if (tocHasContent) {
      setActive(toc[0]?.id);
      setOverflown(overFlownInitialState);
      setAutoExpand(autoExpandInitialState);
    }
  }, [toc]);
  useEffect(() => {
    // Functions that must only be run once at toc change
    if (tocHasContent) {
      appendRelativeToc(getRelativeToc(toc, tocHasContent));
      scrollToHash(hash);
      return handlePageScroll(toc, active, setActive);
    }
    return undefined;
  }, [toc]);
  useEffect(() => {
    // Functions with cross dependencies
    if (tocHasContent) {
      checkAutoExpand(overflown, autoExpand, setAutoExpand);
      return handleTocScroll(ref, overflown, setOverflown);
    }
    return undefined;
  }, [toc, overflown, autoExpand, ref]);
  return /*#__PURE__*/React.createElement("div", {
    className: "ebds-toc-sticky-container",
    ref: ref
  }, tocHasContent && /*#__PURE__*/React.createElement("div", {
    className: "ebds-toc"
  }, overflown.top && /*#__PURE__*/React.createElement("div", {
    className: "ebds-toc-scroll-indicator-top"
  }, /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "up"
  })), /*#__PURE__*/React.createElement("ul", {
    "data-level": "1"
  }, getHeadingListConditionally([...toc], active, autoExpand.active)), overflown.bottom && /*#__PURE__*/React.createElement("div", {
    className: "ebds-toc-scroll-indicator-bottom"
  }, /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "down"
  }))));
});
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true
    };
  }
  static componentDidCatch(error, info) {
    console.error(error, info.componentStack);
  }
  render() {
    const {
      state,
      props
    } = this;
    if (state.hasError) {
      // You can render any custom fallback UI
      return props.fallback;
    }
    return props.children;
  }
}

// Main wrapper, ensures when it throws it doesn't break entire website.
const TableOfContents = ({
  toc,
  route,
  hash
}) => {
  const tocRef = useRef(null);
  const [tocContent, setTocContent] = useState([]);
  const tocHasContent = tocContent[0]?.children?.length > 0;
  useEffect(() => {
    // Apply/fetch content when the pages headings are available
    if (tocRef.current) {
      setTocContent(toc || getParsedToc());
    }
  }, [tocRef, toc, route]);
  if (tocRef.current && !tocHasContent) {
    console.warn('No ToC content provided and no valid headings structure found when attempting to parse the page.');
    return null;
  }
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallback: null
  }, /*#__PURE__*/React.createElement(TableOfContentsMain, {
    toc: tocContent,
    tocHasContent: tocHasContent,
    hash: hash,
    ref: tocRef
  }));
};
export default TableOfContents;